import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import { openFilePreview } from '../common/FilePreviewModal';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import PopoutWindow from '../common/PopoutWindow';
import AccountsSettings from './AccountsSettings';
import '../documentation/JobList.css';
import FundRequestReleaseModal from './FundRequestReleaseModal';

const API_BASE = '/api/documentation/jobs';

const LEFT_ALIGNED_KEYS = ['idf_no', 'customer_name', 'bl_no'];

const toDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value).toUpperCase();
};

const renderCell = (row, field) => {
  const value = row[field.key];
  if (field.key === 'dg') {
    return value ? (
      <i className="fa-solid fa-check" style={{ color: '#2e7d32' }} title="Dangerous Goods" />
    ) : (
      <i className="fa-solid fa-xmark" style={{ color: '#c62828' }} />
    );
  }
  if (field.key === 'etb') {
    return <span style={{ color: 'red', fontWeight: 600 }}>{toDisplay(value)}</span>;
  }
  if (field.key && String(field.key).toLowerCase().includes('customer')) {
    return <strong>{toDisplay(value)}</strong>;
  }
  if (field.key === 'duty_paid') {
    if (!value) return <span></span>;
    const text = String(value).toUpperCase();
    if (text === 'PAID') {
      return <span style={{ animation: 'paidFlash 1s infinite', fontWeight: 600, padding: '2px 6px', borderRadius: 4, display: 'inline-block' }}>{text}</span>;
    }
    return <span style={{ fontWeight: 600 }}>{text}</span>;
  }
  return toDisplay(value);
};

const FILTER_CARD_COLORS = [
  { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
  { bg: '#dcfce7', border: '#86efac', text: '#166534' },
  { bg: '#fef9c3', border: '#fde047', text: '#854d0e' },
  { bg: '#fce7f3', border: '#f9a8d4', text: '#9a174d' },
  { bg: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
  { bg: '#ffedd5', border: '#fdba74', text: '#9a3412' },
  { bg: '#cffafe', border: '#67e8f9', text: '#155e75' },
  { bg: '#f0fdf4', border: '#4ade80', text: '#14532d' },
];

const FundRequest = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('FUND REQUEST', 'can_view');
  const canEdit = hasPermission('FUND REQUEST', 'can_edit') || hasPermission('FUND REQUEST', 'can_create');
  const canDelete = hasPermission('FUND REQUEST', 'can_delete');

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [shipmentTypeId, setShipmentTypeId] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingGroups, setPendingGroups] = useState([]);
  const [fupGroups, setFupGroups] = useState([]);
  const [releasedGroups, setReleasedGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [releaseModal, setReleaseModal] = useState({ open: false, row: null });
  const [undoConfirmDialog, setUndoConfirmDialog] = useState({ show: false, row: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [hoveredRowId, setHoveredRowId] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/fund-request-customers/?released=1`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch customers');
      const data = await res.json();
      setCustomers((Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), text: c.short_name || c.name, name: c.name })));
    } catch (e) {
      toast.error(e.message || 'Failed to load customers');
    }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    const buildUrl = (tab) => {
      const params = new URLSearchParams();
      if (appliedSearch) params.append('search', appliedSearch);
      if (tab === 'released' && customerId) params.append('customer_id', customerId);
      return `${API_BASE}/fund-request/?${params.toString()}&tab=${tab}`;
    };
    const fetchTab = async (tab) => {
      const res = await fetch(buildUrl(tab), { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch fund request report');
      const data = await res.json();
      return data.groups || [];
    };
    try {
      // Render the active tab first, then load the other tabs in the
      // background so their tab counts populate without blocking the page.
      const setters = { pending: setPendingGroups, fup_email: setFupGroups, released: setReleasedGroups };
      setters[activeTab](await fetchTab(activeTab));
      setLoading(false);
      const otherTabs = ['pending', 'fup_email', 'released'].filter(t => t !== activeTab);
      const results = await Promise.all(otherTabs.map(fetchTab));
      otherTabs.forEach((tab, i) => setters[tab](results[i]));
    } catch (e) {
      toast.error(e.message || 'Failed to fetch fund request report');
      setPendingGroups([]);
      setFupGroups([]);
      setReleasedGroups([]);
      setLoading(false);
    }
  }, [appliedSearch, customerId, activeTab]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const groups = activeTab === 'pending' ? pendingGroups : activeTab === 'fup_email' ? fupGroups : releasedGroups;
  const pendingCount = pendingGroups.reduce((sum, g) => sum + (g.rows?.length || 0), 0);
  const fupEmailCount = fupGroups.reduce((sum, g) => sum + (g.rows?.length || 0), 0);
  const releasedCount = releasedGroups.reduce((sum, g) => sum + (g.rows?.length || 0), 0);

  const customerFilteredGroups = customerId ? groups.filter(g => String(g.customer_id) === customerId) : groups;
  const displayGroups = shipmentTypeId
    ? customerFilteredGroups.filter(g => (g.shipment_type || 'OTHER') === shipmentTypeId)
    : customerFilteredGroups;
  const totalCount = displayGroups.reduce((sum, g) => sum + (g.rows?.length || 0), 0);

  // Regroup by Shipment Type (merging rows across customers) for display.
  const shipmentTypeGroups = useMemo(() => {
    const map = new Map();
    displayGroups.forEach(g => {
      const key = g.shipment_type || 'OTHER';
      if (!map.has(key)) {
        map.set(key, { shipment_type: key, fields: g.fields, rows: [] });
      }
      map.get(key).rows.push(...(g.rows || []));
    });
    return Array.from(map.values()).sort((a, b) => (a.shipment_type || '').localeCompare(b.shipment_type || ''));
  }, [displayGroups]);

  const getSortedRows = useCallback((rows) => {
    if (!sortColumn) return rows;
    const sorted = [...rows].sort((a, b) => {
      const av = String(a[sortColumn] ?? '').toUpperCase();
      const bv = String(b[sortColumn] ?? '').toUpperCase();
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [sortColumn, sortDirection]);

  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const handleCustomerChange = (val) => { setCustomerId(val || ''); setSearch(''); setAppliedSearch(''); };
  const handleShipmentTypeChange = (val) => setShipmentTypeId(prev => (prev === val ? '' : val));
  const handleApplySearch = () => setAppliedSearch(search);
  const handleClear = () => { setSearch(''); setAppliedSearch(''); };

  const handleToggle = (rowId, field, value) => {
    if (!canEdit) { toast.error('You do not have permission to update this.'); return; }
    if (field === 'release' && value) {
      const row = groups.flatMap(g => g.rows).find(r => r.id === rowId);
      if (row && !row.fup_email) {
        toast.error('F/UP Email must be sent before releasing the fund request.');
        return;
      }
      if (row) {
        const group = groups.find(g => g.rows.some(r => r.id === rowId));
        setReleaseModal({ open: true, row: { ...row, customer_name: group?.customer_name || '', shipment_type: group?.shipment_type || '' } });
      }
      return;
    }
    doToggle(rowId, field, value);
  };

  const doToggle = async (rowId, field, value) => {
    setTogglingId(`${rowId}-${field}`);
    try {
      const res = await fetch(`${API_BASE}/fund-request-toggle/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ job_id: rowId, field, value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update.');
      if (field === 'release' || field === 'email_sent' || field === 'fup_email') {
        // Row moves between tabs; refetch.
        fetchReport();
      } else {
        const updater = prev => prev.map(g => ({
          ...g,
          rows: g.rows.map(r => (r.id === rowId ? { ...r, [field]: value } : r)),
        }));
        setPendingGroups(updater);
        setReleasedGroups(updater);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to update.');
    } finally {
      setTogglingId(null);
    }
  };

  const handleUndoRelease = () => {
    const row = undoConfirmDialog.row;
    if (!row) return;
    doToggle(row.id, 'release', false);
    setUndoConfirmDialog({ show: false, row: null });
  };

  if (!canView) {
    return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Fund Request.</p></div>;
  }


  return (<>
    <style>{`
      @keyframes etaFlash {
        0%, 100% { background-color: #ffcdd2; }
        50% { background-color: #ef9a9a; }
      }
      @keyframes paidFlash {
        0%, 100% { background-color: #ffe0b2; }
        50% { background-color: #ff9800; }
      }
      @keyframes storageFlash {
        0%, 100% { color: #c62828; }
        50% { color: #ef9a9a; }
      }
    `}</style>
    <div className="client-reports-page" style={{ padding: 16 }}>
      <div className="list-header">
        <h2 style={{ margin: 0, color: '#333' }}>Fund Request {customerId ? `(${totalCount})` : ''}</h2>
      </div>

      {activeTab !== 'released' && (() => {
        const customerCounts = groups.reduce((acc, g) => {
          if (!g.customer_id) return acc;
          if (!acc[g.customer_id]) acc[g.customer_id] = { name: g.customer_name, short_name: g.customer_short_name, count: 0 };
          acc[g.customer_id].count += (g.rows?.length || 0);
          return acc;
        }, {});
        const customers = Object.entries(customerCounts).sort((a, b) => ((a[1].short_name || a[1].name) || '').localeCompare((b[1].short_name || b[1].name) || ''));
        const allCount = groups.reduce((sum, g) => sum + (g.rows?.length || 0), 0);
        if (customers.length === 0) return null;
        const cardColors = [
          { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
          { bg: '#dcfce7', border: '#86efac', text: '#166534' },
          { bg: '#fef9c3', border: '#fde047', text: '#854d0e' },
          { bg: '#fce7f3', border: '#f9a8d4', text: '#9a174d' },
          { bg: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
          { bg: '#ffedd5', border: '#fdba74', text: '#9a3412' },
          { bg: '#cffafe', border: '#67e8f9', text: '#155e75' },
          { bg: '#f0fdf4', border: '#4ade80', text: '#14532d' },
        ];
        const isAllActive = !customerId;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, justifyContent: 'center' }}>
            <button
              onClick={() => setCustomerId('')}
              style={{
                background: isAllActive ? '#374151' : '#f3f4f6',
                border: '2px solid #9ca3af',
                borderRadius: 8,
                padding: '6px 14px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                minWidth: 70,
              }}
            >
              <span style={{ fontSize: 10, color: isAllActive ? '#e0e0e0' : '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>ALL</span>
              <span style={{ fontWeight: 'bold', fontSize: 14, color: isAllActive ? '#fff' : '#374151', lineHeight: 1 }}>{allCount}</span>
            </button>
            {customers.map(([cid, c], i) => {
              const isActive = customerId === String(cid);
              const colors = cardColors[i % cardColors.length];
              return (
                <button
                  key={cid}
                  onClick={() => setCustomerId(isActive ? '' : String(cid))}
                  style={{
                    background: isActive ? colors.text : colors.bg,
                    border: `2px solid ${colors.border}`,
                    borderRadius: 8,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 70,
                  }}
                >
                  <span title={c.name} style={{ fontSize: 10, color: isActive ? '#fff' : colors.text, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.short_name || c.name}</span>
                  <span style={{ fontWeight: 'bold', fontSize: 14, color: isActive ? '#fff' : colors.text, lineHeight: 1 }}>{c.count}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {(() => {
        const shipmentTypeCounts = customerFilteredGroups.reduce((acc, g) => {
          const key = g.shipment_type || 'OTHER';
          if (!acc[key]) acc[key] = 0;
          acc[key] += (g.rows?.length || 0);
          return acc;
        }, {});
        const shipmentTypes = Object.entries(shipmentTypeCounts).sort((a, b) => a[0].localeCompare(b[0]));
        const allCount = customerFilteredGroups.reduce((sum, g) => sum + (g.rows?.length || 0), 0);
        if (shipmentTypes.length === 0) return null;
        const isAllActive = !shipmentTypeId;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, justifyContent: 'center' }}>
            <button
              onClick={() => setShipmentTypeId('')}
              style={{
                background: isAllActive ? '#374151' : '#f3f4f6',
                border: '2px solid #9ca3af',
                borderRadius: 8,
                padding: '6px 14px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                minWidth: 70,
              }}
            >
              <span style={{ fontSize: 10, color: isAllActive ? '#e0e0e0' : '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>ALL TYPES</span>
              <span style={{ fontWeight: 'bold', fontSize: 14, color: isAllActive ? '#fff' : '#374151', lineHeight: 1 }}>{allCount}</span>
            </button>
            {shipmentTypes.map(([stype, count], i) => {
              const isActive = shipmentTypeId === stype;
              const colors = FILTER_CARD_COLORS[i % FILTER_CARD_COLORS.length];
              return (
                <button
                  key={stype}
                  onClick={() => handleShipmentTypeChange(stype)}
                  style={{
                    background: isActive ? colors.text : colors.bg,
                    border: `2px solid ${colors.border}`,
                    borderRadius: 8,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 70,
                  }}
                >
                  <span style={{ fontSize: 10, color: isActive ? '#fff' : colors.text, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{stype}</span>
                  <span style={{ fontWeight: 'bold', fontSize: 14, color: isActive ? '#fff' : colors.text, lineHeight: 1 }}>{count}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      <div className="toolbar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        {activeTab === 'released' && (
          <div style={{ width: 240 }}>
            <Select2Dropdown
              options={customers}
              value={customerId}
              onChange={(val) => setCustomerId(val || '')}
              displayKey="text"
              valueKey="id"
              placeholder="Search customer (all released)..."
              required={false}
              searchable={true}
              clearable={true}
            />
          </div>
        )}
        <input
          className="search-input"
          placeholder="Search IDF or BL..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleApplySearch(); }}
          style={{ width: 40 }}
        />
        <button className="add-btn" style={{ background: '#007bff' }} onClick={handleApplySearch} >Search</button>
        <button className="add-btn" style={{ background: '#6c757d' }} onClick={handleClear} >Clear</button>
        <button className="add-btn" style={{ background: '#3f6b32' }} onClick={fetchReport} >Refresh</button>
      </div>

      <div className="custom-tabs-container" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex' }}>
          <button
            className={`custom-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending ({pendingCount})
          </button>
          <button
            className={`custom-tab ${activeTab === 'fup_email' ? 'active' : ''}`}
            onClick={() => setActiveTab('fup_email')}
          >
            Email Sent ({fupEmailCount})
          </button>
          <button
            className={`custom-tab ${activeTab === 'released' ? 'active' : ''}`}
            onClick={() => setActiveTab('released')}
          >
            Released ({releasedCount})
          </button>
        </div>
        {canEdit && (
          <button
            type="button"
            className="btn-icon"
            title="Accounts Settings"
            onClick={() => setSettingsOpen(true)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: '#6b7280', padding: '4px 8px' }}
          >
            <i className="fa fa-cog"></i>
          </button>
        )}
      </div>

      {loading && <div className="loading-indicator">Loading...</div>}

      {!loading && shipmentTypeGroups.length === 0 && (
        <div className="no-data" style={{ padding: 24, textAlign: 'center', background: '#f8f9fa', borderRadius: 6 }}>
          No records found.
        </div>
      )}

      {!loading && shipmentTypeGroups.map(group => (
        <div key={group.shipment_type} style={{ marginBottom: 24 }}>
          <div style={{
            background: '#e5def1',
            padding: '8px 12px',
            borderRadius: '6px 6px 0 0',
            fontWeight: 'bold',
            fontSize: 14,
            color: '#333'
          }}>
            {group.shipment_type || 'OTHER'} ({group.rows?.length || 0})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', minWidth: 1100, tableLayout: 'auto', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
              <thead>
                <tr>
                  {group.fields.map(f => (
                    <th
                      key={f.key}
                      onClick={() => handleSort(f.key)}
                      style={{ whiteSpace: 'nowrap', fontSize: 12, cursor: 'pointer', userSelect: 'none', border: '1px solid #ddd', padding: '6px', textAlign: LEFT_ALIGNED_KEYS.includes(f.key) ? 'left' : 'center' }}
                    >
                      {f.key === 'eta' ? (
                        <>ETA{(group.shipment_type || '').toUpperCase() === 'IMPORT-SEA' && (<><br /><span style={{ color: 'red', fontWeight: 'bold' }}>ETB</span></>)}</>
                      ) : f.key === 'carry_in_end_date' ? (
                        <>C/IN END DATE<br /><span style={{ color: '#2e7d32' }}>DISCHARGE DATE</span></>
                      ) : f.label}
                      {sortColumn === f.key && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                    </th>
                  ))}
                  {activeTab === 'released' && canDelete && (
                    <th style={{ whiteSpace: 'nowrap', fontSize: 12, border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>ACTION</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {getSortedRows(group.rows || []).map((row, rowIndex) => (
                  <tr
                    key={row.id}
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    style={{ backgroundColor: row.id === hoveredRowId ? '#bbdefb' : (rowIndex % 2 === 0 ? '#e3f2fd' : '#fff') }}
                  >
                    {group.fields.map(f => {
                      if (f.key === 'email_sent' && activeTab === 'pending') {
                        const isDisabled = !canEdit || togglingId === `${row.id}-${f.key}`;
                        return (
                          <td key={f.key} style={{ textAlign: 'center', border: '1px solid #ddd', padding: '6px' }}>
                            <input
                              type="checkbox"
                              checked={!!row[f.key]}
                              disabled={isDisabled}
                              onChange={e => handleToggle(row.id, f.key, e.target.checked)}
                            />
                          </td>
                        );
                      }
                      if (f.key === 'email_sent' && (activeTab === 'fup_email' || activeTab === 'released')) {
                        return (
                          <td key={f.key} style={{ textAlign: 'center', whiteSpace: 'pre-line', fontSize: 12, border: '1px solid #ddd', padding: '6px' }}>
                            {toDisplay(row[f.key])}
                          </td>
                        );
                      }
                      if ((f.key === 'fup_email' || f.key === 'release') && activeTab === 'released') {
                        return (
                          <td key={f.key} style={{ textAlign: 'center', whiteSpace: 'pre-line', fontSize: 12, border: '1px solid #ddd', padding: '6px' }}>
                            {toDisplay(row[f.key])}
                          </td>
                        );
                      }
                      if (f.key === 'fup_email' || f.key === 'release') {
                        const isDisabled = !canEdit || togglingId === `${row.id}-${f.key}` || (f.key === 'release' && !row.fup_email);
                        let title = '';
                        if (f.key === 'release' && !row.fup_email) title = 'F/UP Email must be sent first';
                        return (
                          <td key={f.key} style={{ textAlign: 'center', border: '1px solid #ddd', padding: '6px' }}>
                            <input
                              type="checkbox"
                              checked={!!row[f.key]}
                              disabled={isDisabled}
                              onChange={e => handleToggle(row.id, f.key, e.target.checked)}
                              title={title}
                            />
                          </td>
                        );
                      }
                      if (f.key === 'bl_type') {
                        const blUrl = row.bl_url;
                        return (
                          <td key={f.key} style={{ fontSize: 12, whiteSpace: 'pre-line', textAlign: 'center', border: '1px solid #ddd', padding: '6px' }}>
                            {blUrl ? (
                              <a
                                href={blUrl}
                                onClick={e => { e.preventDefault(); openFilePreview(blUrl, row.bl_type || 'BL Document'); }}
                                style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                              >
                                {renderCell(row, f)}
                              </a>
                            ) : (
                              renderCell(row, f)
                            )}
                          </td>
                        );
                      }
                      if (f.key === 'carry_in_end_date') {
                        return (
                          <td key={f.key} style={{ fontSize: 12, whiteSpace: 'nowrap', textAlign: 'center', border: '1px solid #ddd', padding: '6px' }}>
                            <div>{renderCell(row, f)}</div>
                            {row.discharge_date && (
                              <div style={{ color: '#2e7d32', fontWeight: 600 }}>{row.discharge_date}</div>
                            )}
                          </td>
                        );
                      }
                      if (f.key === 'storage_date' || f.key === 'demurrage_date' || f.key === 'cwr_date') {
                        const startedKey = { storage_date: 'storage_started', demurrage_date: 'demurrage_started', cwr_date: 'cwr_started' }[f.key];
                        const startedLabel = { storage_date: 'Storage started', demurrage_date: 'Demurrage started', cwr_date: 'CWR started' }[f.key];
                        const started = !!row[startedKey];
                        return (
                          <td
                            key={f.key}
                            title={started ? startedLabel : undefined}
                            style={{ fontSize: 12, whiteSpace: f.key === 'storage_date' ? 'pre-line' : 'nowrap', textAlign: 'center', border: '1px solid #ddd', padding: '6px' }}
                          >
                            <span style={started ? { fontWeight: 'bold', animation: 'storageFlash 1.5s infinite' } : {}}>
                              {renderCell(row, f)}
                            </span>
                          </td>
                        );
                      }
                      const preLine = f.key === 'weight_cbm' || f.key === 'container_size';
                      if (f.key === 'eta') {
                        const flash = !!row.manifest_ready_date;
                        return (
                          <td
                            key={f.key}
                            title={flash ? `MANIFEST OUT: ${row.manifest_ready_date}` : undefined}
                            style={{ fontSize: 12, whiteSpace: 'nowrap', textAlign: 'center', border: '1px solid #ddd', padding: '6px', ...(flash ? { backgroundColor: '#ffcdd2', animation: 'etaFlash 1.5s infinite' } : {}) }}
                          >
                            <div>{renderCell(row, f)}</div>
                            {row.etb && (
                              <div style={{ color: 'red', fontWeight: 'bold' }}>{row.etb}</div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={f.key} style={{ fontSize: 12, whiteSpace: preLine ? 'pre-line' : 'nowrap', border: '1px solid #ddd', padding: '6px', textAlign: LEFT_ALIGNED_KEYS.includes(f.key) ? 'left' : 'center' }}>
                          {renderCell(row, f)}
                        </td>
                      );
                    })}
                    {activeTab === 'released' && canDelete && (
                      <td style={{ textAlign: 'center', border: '1px solid #ddd', padding: '6px' }}>
                        <button
                          className="icon-btn"
                          title="Undo Release"
                          style={{ color: 'darkorange' }}
                          onClick={() => setUndoConfirmDialog({ show: true, row })}
                        >
                          <i className="fa fa-undo" style={{ marginRight: 4 }}></i>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {(!group.rows || group.rows.length === 0) && (
                  <tr><td colSpan={(group.fields.length || 1) + (activeTab === 'released' && canDelete ? 1 : 0)} className="no-data">No records</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
    {releaseModal.open && (
      <FundRequestReleaseModal
        row={releaseModal.row}
        onClose={() => setReleaseModal({ open: false, row: null })}
        onSent={() => doToggle(releaseModal.row.id, 'release', true)}
      />
    )}
    {undoConfirmDialog.show && (
      <div className="modal-overlay">
        <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>
          <div className="dialog-header" style={{ background: '#dc3545', color: '#fff', borderRadius: '4px 4px 0 0' }}>
            <h3 style={{ margin: 0, color: '#fff' }}>Undo Release</h3>
          </div>
          <div className="dialog-body" style={{ padding: '20px' }}>
            <p style={{ margin: 0, fontSize: '1rem' }}>
              Are you sure you want to undo the release for IDF <strong>{undoConfirmDialog.row?.idf_no || '-'}</strong>? This will mark it as not released.
            </p>
          </div>
          <div className="dialog-footer">
            <button className="dialog-btn dialog-btn-primary" onClick={handleUndoRelease}>
              Yes
            </button>
            <button className="dialog-btn dialog-btn-secondary" onClick={() => setUndoConfirmDialog({ show: false, row: null })}>
              No
            </button>
          </div>
        </div>
      </div>
    )}

    {settingsOpen && (
      <PopoutWindow
        name="AccountsSettings_FundRequest"
        title="Accounts Settings"
        width={1200}
        height={800}
        zoom={1}
        onClose={() => setSettingsOpen(false)}
      >
        <AccountsSettings />
      </PopoutWindow>
    )}
    </>
  );
};

export default FundRequest;
